// src/components/Patient/PatientDashboard.jsx
import React, { useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Skeleton,
  Tag,
  Space,
  Button,
  List,
} from "antd";
import {
  CalendarOutlined,
  FileTextOutlined,
  PlusOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  IdcardOutlined,
  GlobalOutlined,
  SolutionOutlined,
  TeamOutlined,
  ToolOutlined,
  EyeOutlined,
  // HeartOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatientDetails } from "../../Redux/Slice/Doctor/Patients";
import { fetchPatientTodayAppointments } from "../../Redux/Slice/Patient/Appointments";
import { fetchMedicine } from "../../Redux/Slice/Patient/Medicine";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

/* Info Tile Component */
function InfoTile({ icon, label, value }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 10,
        padding: "10px 12px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        minHeight: 70,
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: "#888",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {icon} <span>{label}</span>
      </span>
      <span style={{ fontWeight: 600, fontSize: 14, marginTop: 6 }}>
        {value || "-"}
      </span>
    </div>
  );
}

const PatientDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const patientId = user?.patientId;

  const { data, loading } = useSelector(
    (state) => state.patient.patientDetails
  );
  const { data: appointmentData, loading: appointmentLoading } = useSelector(
    (state) => state.patientAppointment.today
  );
  const { data: fetchData } = useSelector((state) => state.medicine.fetch);
  const { data: notificationData } = useSelector(
    (state) => state.call.notification || {}
  );

  const videoCalls = notificationData?.createdVideoCalls || [];

  useEffect(() => {
    if (patientId) dispatch(fetchPatientDetails(patientId));
  }, [dispatch, patientId]);

  useEffect(() => {
    if (patientId) dispatch(fetchPatientTodayAppointments());
  }, [patientId, dispatch]);

  useEffect(() => {
    if (patientId) dispatch(fetchMedicine({ patientId }));
  }, [dispatch, patientId]);

  // Patient data
  const patient = data?.data?.[0]?.demographics || {};
  const activeMeds =
    fetchData?.data?.medicines?.filter((med) => med.status === "Active") || [];

  // Today's Appointments (show only latest one)
  const sortedAppointments =
    appointmentData?.data
      ?.slice()
      .sort(
        (a, b) => new Date(b.appointmentTime) - new Date(a.appointmentTime)
      ) || [];
  const latestAppointment = sortedAppointments[0];

  // Quick Action Cards
  const cardData = [
    {
      title: "Book Appointment",
      icon: <PlusOutlined style={{ fontSize: 20, color: "#1890ff" }} />,
      action: () => navigate("/book-appointment"),
      bgColor: "#e6f7ff",
    },
    // {
    //   title: "Vitals",
    //   icon: <HeartOutlined style={{ fontSize: 20, color: "#eb2f96" }} />,
    //   action: () => navigate("/patient-records"),
    //   bgColor: "#fff0f6",
    // },
    {
      title: "History",
      icon: <CalendarOutlined style={{ fontSize: 20, color: "#722ed1" }} />,
      action: () =>
        navigate("/patient-appointment", { state: { tab: "past" } }),
      bgColor: "#f9f0ff",
    },
    {
      title: "Reports",
      icon: <FileTextOutlined style={{ fontSize: 20, color: "#13c2c2" }} />,
      action: () => navigate("/patient-reports"),
      bgColor: "#e6fffb",
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        height: "80vh",
        background: "#f4f6f9",
      }}
    >
      {/* LEFT SIDE → Patient Profile */}
      <div
        style={{
          width: "30%",
          background: "#fff",
          paddingRight: "24px",
          borderRight: "1px solid #f0f0f0",
          boxShadow: "2px 0 6px rgba(0,0,0,0.05)",
          overflowY: "auto",
        }}
      >
        <Skeleton loading={loading} active avatar paragraph={{ rows: 1 }}>
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              background: "linear-gradient(135deg, #f7f0ffff, #e6fffb)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              padding: "14px",
            }}
          >
            {/* Header */}
            <div
              style={{
                marginBottom: 20,
                display: "flex",
                gap: 10,
              }}
            >
              <Avatar
                size={50}
                icon={<UserOutlined />}
                style={{ backgroundColor: "#1890ff" }}
              />
              <div>
                <Title level={5} style={{ marginTop: 0 }}>
                  {patient?.name || "N/A"}
                </Title>
                <Tag color="blue" style={{ fontSize: 13 }}>
                  MRD No: {patient?.mrdNo || "PID"}
                </Tag>
              </div>
            </div>

            {/* Info Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px 12px",
              }}
            >
              <InfoTile
                icon={<CalendarOutlined style={{ color: "#a2a5b0ff" }} />}
                label="DOB"
                value={patient?.dob}
              />
              <InfoTile
                icon={<UserOutlined style={{ color: "#a2a5b0ff" }} />}
                label="Age / Gender"
                value={`${patient?.age || "-"} / ${patient?.gender || "-"}`}
              />
              <InfoTile
                icon={<PhoneOutlined style={{ color: "#a2a5b0ff" }} />}
                label="Phone"
                value={patient?.phoneNo}
              />
              <InfoTile
                icon={<MailOutlined style={{ color: "#a2a5b0ff" }} />}
                label="Email"
                value={patient?.emailId}
              />
              <InfoTile
                icon={<HomeOutlined style={{ color: "#a2a5b0ff" }} />}
                label="Address"
                value={patient?.address}
              />
              <InfoTile
                icon={<IdcardOutlined style={{ color: "#a2a5b0ff" }} />}
                label="National ID"
                value={patient?.nationalId}
              />
              <InfoTile
                icon={<GlobalOutlined style={{ color: "#a2a5b0ff" }} />}
                label="Nationality"
                value={patient?.nationality}
              />
              <InfoTile
                icon={<TeamOutlined style={{ color: "#a2a5b0ff" }} />}
                label="Marital Status"
                value={patient?.maritalStatus}
              />
              <InfoTile
                icon={<SolutionOutlined style={{ color: "#a2a5b0ff" }} />}
                label="Visa Type"
                value={patient?.visaType}
              />
              <InfoTile
                icon={<ToolOutlined style={{ color: "#a2a5b0ff" }} />}
                label="Occupation"
                value={patient?.occupation}
              />
            </div>
          </Card>
        </Skeleton>
      </div>

      {/* RIGHT SIDE → Dashboard */}
      <div
        style={{
          flex: 1,
          padding: "20px",
          overflowY: "auto",
        }}
      >
        {/* Quick Action Buttons */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {cardData.map((card, index) => (
            <Col xs={12} md={6} key={index}>
              <Card
                hoverable
                onClick={card.action}
                style={{
                  textAlign: "center",
                  borderRadius: "12px",
                  backgroundColor: card.bgColor,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  transition: "all 0.3s",
                }}
                styles={{ body: { padding: "18px" } }}
              >
                <Space
                  direction="vertical"
                  align="center"
                  style={{ width: "100%" }}
                >
                  {card.icon}
                  <Text strong style={{ fontSize: 14 }}>
                    {card.title}
                  </Text>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Latest Appointment */}
        <Card
          title="Today's Appointment"
          extra={
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() =>
                navigate("/patient-appointment", { state: { tab: "today" } })
              }
            >
              View All
            </Button>
          }
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            marginBottom: 20,
          }}
        >
          {appointmentLoading ? (
            <Skeleton active />
          ) : latestAppointment ? (
            <List>
              <List.Item
                actions={[
                  (() => {
                    const activeCall = videoCalls.find(
                      (c) => c.appointmentId === latestAppointment.appointmentId
                    );

                    console.log("activeCall", activeCall);

                    if (activeCall) {
                      if (
                        activeCall.status === "active" ||
                        activeCall.status === "waiting"
                      ) {
                        // ✅ Show join button only when call is active
                        return (
                          <Button
                            type="primary"
                            size="small"
                            onClick={() =>
                              navigate("/call", {
                                state: {
                                  patientId,
                                  roomCode: activeCall.roomCode,
                                },
                              })
                            }
                          >
                            Join Call
                          </Button>
                        );
                      } else {
                        // 🔒 Show status tag when call exists but not active
                        return <Tag color="red">{activeCall.status}</Tag>;
                      }
                    }

                    // ❌ No call at all
                    return <Tag color="red">No Active Call</Tag>;
                  })(),
                ]}
              >
                <List.Item.Meta
                  title={`${latestAppointment.doctorName} • ${latestAppointment.appointmentTime}`}
                  description={
                    <Tag
                      color={
                        latestAppointment.confirmStatus === "Unconfirmed"
                          ? "orange"
                          : "green"
                      }
                    >
                      {latestAppointment.confirmStatus}
                    </Tag>
                  }
                />
              </List.Item>
            </List>
          ) : (
            <Text>No appointments today</Text>
          )}
        </Card>

        {/* Active Medicines (Redesigned) */}
        <Card
          title="Active Medicines"
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          {activeMeds.length === 0 && <Text>No active medicines</Text>}
          <Row gutter={[16, 16]}>
            {activeMeds.map((med, index) => (
              <Col xs={24} md={12} key={index}>
                <Card
                  size="small"
                  style={{
                    borderRadius: 12,
                    background: "#f6ffed",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                  }}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Space align="center">
                      <MedicineBoxOutlined style={{ color: "#52c41a" }} />
                      <Text strong>{med.medicineName}</Text>
                    </Space>
                    <Text type="secondary">{med.dosage}</Text>
                    {/* <Text>
                      <strong>Order Type:</strong> {med.orderType || "-"}
                    </Text>
                    <Text>
                      <strong>Frequency:</strong> {med.frequency || "-"}
                    </Text>
                    <Text>
                      <strong>Days:</strong> {med.noOfDays || "-"}
                    </Text>
                    <Text>
                      <strong>Food Instruction:</strong>{" "}
                      {med.foodInstruction || "-"}
                    </Text> */}
                    {med.pdfUrl && (
                      <a
                        href={med.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Prescription
                      </a>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      </div>
    </div>
  );
};

export default PatientDashboard;
